<template>
  <div class="flex gap-2 flex-wrap">
    <InputText
      v-model="searchQuery"
      placeholder="Search actions..."
      class=" w-full flex-1 min-w-[160px] min-h-[44px]"
      icon="pi pi-search"
    />
    <div class="flex gap-2">
      <div class="relative inline-block">
        <Button
          ref="filterButton"
          icon="pi pi-filter"
          severity="secondary"
          class="w-full sm:w-auto min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
          @click="toggleFilter"
          v-tooltip.bottom="'Filter actions'"
        />
        <Badge
          v-if="activeFiltersCount > 0"
          :value="activeFiltersCount"
          severity="info"
          class="absolute -top-2 -right-2"
        />
      </div>
      <Button
        ref="settingsButton"
        icon="pi pi-cog"
        severity="secondary"
        class="w-full sm:w-auto min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
        @click="toggleSettings"
        v-tooltip.bottom="'Card display settings'"
      />
    </div>
  </div>

  <Popover ref="filterPanel" class="w-[calc(100vw-2rem)] sm:w-80 max-w-[400px]">
    <div class="p-3 sm:p-4">
      <div class="mb-4 sm:mb-3">
        <label class="text-sm sm:text-base font-semibold text-(--p-anthracite-500)"
          >Filter by State</label
        >
      </div>
      <div class="space-y-3 sm:space-y-2">
        <div
          v-for="state in stateOptions"
          :key="state.value"
          class="flex items-center min-h-[44px] py-1"
        >
          <Checkbox
            :id="`state-${state.value}`"
            v-model="selectedStates"
            :value="state.value"
            :binary="false"
            class="flex-shrink-0"
          />
          <label
            :for="`state-${state.value}`"
            class="ml-3 sm:ml-2 text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1"
          >
            {{ state.label }}
          </label>
        </div>
      </div>

      <div class="mb-4 sm:mb-3 mt-5 sm:mt-4">
        <label class="text-sm sm:text-base font-semibold text-(--p-anthracite-500)"
          >Filter by KPI Area</label
        >
      </div>
      <div class="space-y-3 sm:space-y-2 max-h-48 sm:max-h-56 overflow-y-auto custom-scrollbar">
        <div
          v-for="kpiArea in kpiAreaOptions"
          :key="kpiArea.value"
          class="flex items-center min-h-[44px] py-1"
        >
          <Checkbox
            :id="`kpi-${kpiArea.value}`"
            v-model="selectedKpiAreas"
            :value="kpiArea.value"
            :binary="false"
            class="flex-shrink-0"
          />
          <label
            :for="`kpi-${kpiArea.value}`"
            class="ml-3 sm:ml-2 text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1"
          >
            {{ kpiArea.label }}
          </label>
        </div>
      </div>

      <div class="mb-4 sm:mb-3 mt-5 sm:mt-4">
        <label class="text-sm sm:text-base font-semibold text-(--p-anthracite-500)"
          >Filter by Due Date</label
        >
      </div>
      <div class="space-y-3 sm:space-y-2">
        <div
          v-for="dueDateOption in dueDateOptions"
          :key="dueDateOption.value"
          class="flex items-center min-h-[44px] py-1"
        >
          <Checkbox
            :id="`due-${dueDateOption.value}`"
            v-model="selectedDueDateFilters"
            :value="dueDateOption.value"
            :binary="false"
            class="flex-shrink-0"
          />
          <label
            :for="`due-${dueDateOption.value}`"
            class="ml-3 sm:ml-2 text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1"
          >
            {{ dueDateOption.label }}
          </label>
        </div>
      </div>

      <div class="mb-4 sm:mb-3 mt-5 sm:mt-4">
        <label class="text-sm sm:text-base font-semibold text-(--p-anthracite-500)"
          >Filter by Retailer</label
        >
      </div>
      <div class="mb-4 sm:mb-3">
        <MultiSelect
          v-model="selectedRetailers"
          :options="retailerOptions"
          option-label="label"
          option-value="id"
          placeholder="Select Retailers..."
          display="chip"
          class="w-full"
          :max-selected-labels="3"
          :selected-items-label="`{0} retailers selected`"
          filter
          :filter-fields="['label', 'bank_code']"
          :filter-placeholder="'Search by name or code...'"
        >
          <template #option="{ option }">
            <div class="flex flex-col justify-between w-full py-1.5 sm:py-1">
              <span class="font-medium text-sm sm:text-base">{{
                option.label || 'Unknown Retailer'
              }}</span>
              <small class="text-xs sm:text-sm text-(--p-anthracite-500)"
                >Code: {{ option.bank_code || option.code || 'Unknown Code' }}</small
              >
            </div>
          </template>
        </MultiSelect>
      </div>

      <div class="mt-5 sm:mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-2">
        <Button
          label="Clear"
          severity="secondary"
          size="small"
          @click="clearFilters"
          class="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        />
        <Button
          label="Apply"
          severity="primary"
          size="small"
          @click="applyFilters"
          class="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        />
      </div>
    </div>
  </Popover>

  <Popover ref="settingsPanel" class="w-[calc(100vw-2rem)] sm:w-96 max-w-[450px]">
    <div class="p-4 sm:p-5">
      <div class="mb-5 sm:mb-4">
        <h4 class="text-base sm:text-lg font-semibold text-(--p-anthracite-500) mb-2">
          Card Display Settings
        </h4>
        <p class="text-sm sm:text-base text-(--p-anthracite-500)">
          Choose which information to show on action cards
        </p>
      </div>

      <div class="space-y-5 sm:space-y-4">
        <div>
          <h5 class="text-sm sm:text-base font-medium text-(--p-anthracite-500) mb-3 sm:mb-2">
            Action Information
          </h5>
          <div class="space-y-3 sm:space-y-2">
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show KPI Area
              </label>
              <Checkbox v-model="displaySettings.showKpiArea" binary inputId="show-kpi" />
            </div>
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show Retailer
              </label>
              <Checkbox v-model="displaySettings.showRetailer" binary inputId="show-retailer" />
            </div>
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show Due Date
              </label>
              <Checkbox v-model="displaySettings.showDueDate" binary inputId="show-due-date" />
            </div>
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show Overdue Warning
              </label>
              <Checkbox
                v-model="displaySettings.showOverdueWarning"
                binary
                inputId="show-overdue"
              />
            </div>
          </div>
        </div>

        <div>
          <h5 class="text-sm sm:text-base font-medium text-(--p-anthracite-500) mb-3 sm:mb-2">
            Card Styling
          </h5>
          <div class="space-y-3 sm:space-y-2">
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show Color Indicator
              </label>
              <Checkbox v-model="displaySettings.showColorIndicator" binary inputId="show-color" />
            </div>
            <div class="flex items-center justify-between min-h-[44px] py-1">
              <label class="text-sm sm:text-base text-(--p-anthracite-500) cursor-pointer flex-1">
                Show State Badge
              </label>
              <Checkbox v-model="displaySettings.showStateBadge" binary inputId="show-state" />
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 sm:mt-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-2">
        <Button
          label="Reset to Default"
          severity="secondary"
          size="small"
          @click="resetDisplaySettings"
          class="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        />
        <Button
          label="Apply"
          severity="primary"
          size="small"
          @click="applyDisplaySettings"
          class="w-full sm:w-auto min-h-[44px] sm:min-h-0"
        />
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Badge from 'primevue/badge'
import useActionFilters from '@/composables/ActionFilters'
import useDisplaySettings from '@/composables/useDisplaySettings'
import { useDebounce } from '@/composables/useDebounce'
import { extractRetailerOptions } from '@/utils/extractors'
import type { KpiArea } from '@/config/types/Action'
import { KPI_AREA_OPTIONS } from '@/config/constants/KpiAreas'
import type { Plan } from '@/config/types/Plan'

const props = defineProps<{
  modelValue?: {
    searchQuery: string
    selectedStates: string[]
    selectedKpiAreas: string[]
    selectedDueDateFilters: string[]
    selectedRetailers: (string | number)[]
  }
  plans?: Plan[]
}>()

const emit = defineEmits<{
  (
    e: 'update:modelValue',
    value: {
      searchQuery: string
      selectedStates: string[]
      selectedKpiAreas: string[]
      selectedDueDateFilters: string[]
      selectedRetailers: (string | number)[]
    },
  ): void
  (e: 'display-settings-changed', settings: typeof displaySettings.value): void
}>()

const filterButton = ref()
const filterPanel = ref()
const settingsButton = ref()
const settingsPanel = ref()

const stateOptions = [
  { value: 'pending', label: 'Open' },
  { value: 'active', label: 'Active' },
  { value: 'complete', label: 'Complete' },
]

const dueDateOptions = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'due-soon', label: 'Due in next 7 days' },
  { value: 'due-later', label: 'Due in more than 7 days' },
]

const kpiAreaOptions = KPI_AREA_OPTIONS

const searchQuery = ref(props.modelValue?.searchQuery || '')
const debouncedSearchQuery = useDebounce(searchQuery, 300)
const selectedStates = ref<string[]>([
  ...(props.modelValue?.selectedStates || ['pending', 'active', 'complete']),
])
const selectedKpiAreas = ref<string[]>([...(props.modelValue?.selectedKpiAreas || [])])
const selectedDueDateFilters = ref<string[]>([...(props.modelValue?.selectedDueDateFilters || [])])
const selectedRetailers = ref<(string | number)[]>([...(props.modelValue?.selectedRetailers || [])])

const retailerOptions = computed(() => {
  if (!props.plans) return []
  return extractRetailerOptions(props.plans, true)
})

const activeFiltersCount = computed(() => {
  let count = 0

  if (debouncedSearchQuery.value.trim()) {
    count++
  }

  if (selectedStates.value.length < 3) {
    count++
  }

  if (selectedKpiAreas.value.length > 0) {
    count++
  }

  if (selectedDueDateFilters.value.length > 0) {
    count++
  }

  if (selectedRetailers.value.length > 0) {
    count++
  }

  return count
})

const { settings: displaySettings, resetSettings: resetDisplaySettings } = useDisplaySettings()

const { filterActions: composableFilterActions } = useActionFilters()

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      if (searchQuery.value !== newValue.searchQuery) {
        searchQuery.value = newValue.searchQuery
      }
      if (JSON.stringify(selectedStates.value) !== JSON.stringify(newValue.selectedStates)) {
        selectedStates.value = [...newValue.selectedStates]
      }
      if (JSON.stringify(selectedKpiAreas.value) !== JSON.stringify(newValue.selectedKpiAreas)) {
        selectedKpiAreas.value = [...newValue.selectedKpiAreas]
      }
      if (
        JSON.stringify(selectedDueDateFilters.value) !==
        JSON.stringify(newValue.selectedDueDateFilters)
      ) {
        selectedDueDateFilters.value = [...newValue.selectedDueDateFilters]
      }
      if (JSON.stringify(selectedRetailers.value) !== JSON.stringify(newValue.selectedRetailers)) {
        selectedRetailers.value = [...newValue.selectedRetailers]
      }
    }
  },
  { deep: true },
)

watch(
  [
    debouncedSearchQuery,
    selectedStates,
    selectedKpiAreas,
    selectedDueDateFilters,
    selectedRetailers,
  ],
  () => {
    const currentValues = {
      searchQuery: debouncedSearchQuery.value,
      selectedStates: selectedStates.value,
      selectedKpiAreas: selectedKpiAreas.value,
      selectedDueDateFilters: selectedDueDateFilters.value,
      selectedRetailers: selectedRetailers.value,
    }

    if (
      props.modelValue &&
      currentValues.searchQuery === props.modelValue.searchQuery &&
      JSON.stringify(currentValues.selectedStates) ===
        JSON.stringify(props.modelValue.selectedStates) &&
      JSON.stringify(currentValues.selectedKpiAreas) ===
        JSON.stringify(props.modelValue.selectedKpiAreas) &&
      JSON.stringify(currentValues.selectedDueDateFilters) ===
        JSON.stringify(props.modelValue.selectedDueDateFilters) &&
      JSON.stringify(currentValues.selectedRetailers) ===
        JSON.stringify(props.modelValue.selectedRetailers)
    ) {
      return
    }

    emit('update:modelValue', currentValues)
  },
  { deep: true },
)

const toggleFilter = (e: Event) => {
  filterPanel.value.toggle(e)
}

const clearFilters = () => {
  selectedStates.value = ['pending', 'active', 'complete']
  selectedKpiAreas.value = []
  selectedDueDateFilters.value = []
  selectedRetailers.value = []
  searchQuery.value = ''
  filterPanel.value.hide()
}

const applyFilters = () => {
  filterPanel.value.hide()
}

const toggleSettings = (e: Event) => {
  settingsPanel.value.toggle(e)
}

const applyDisplaySettings = () => {
  settingsPanel.value.hide()
  emit('display-settings-changed', displaySettings.value)
}

const filterActions = (
  actions: {
    title: string
    state: string
    kpiArea?: string
    dueDate: string
    planId?: string | number
  }[],
) => {
  const filterState = {
    searchQuery: debouncedSearchQuery.value,
    selectedStates: selectedStates.value,
    selectedKpiAreas: selectedKpiAreas.value,
    selectedDueDateFilters: selectedDueDateFilters.value,
    selectedRetailers: selectedRetailers.value,
  }

  const actionArray = actions.map((action) => ({
    id: 0,
    title: action.title,
    dueDate: action.dueDate,
    state: action.state as 'pending' | 'active' | 'complete',
    colorHex: '#60a5fa',
    kpiArea: action.kpiArea as KpiArea | undefined,
    details: '',
  }))

  const filtered = composableFilterActions(actionArray, filterState)

  let retailerFiltered = filtered
  if (selectedRetailers.value.length > 0 && props.plans) {
    const selectedRetailerIds = new Set(selectedRetailers.value)
    retailerFiltered = filtered.filter((action) => {
      const plan = props.plans?.find((p) => p.actions.some((a) => a.id === action.id))
      if (!plan || !plan.retailers || plan.retailers.length === 0) return false

      return plan.retailers.some((retailer) => selectedRetailerIds.has(retailer.id))
    })
  }

  return retailerFiltered.map((action) => ({
    title: action.title,
    state: action.state,
    kpiArea: action.kpiArea,
    dueDate: action.dueDate,
  }))
}

defineExpose({
  filterActions,
  searchQuery,
  selectedStates,
  selectedKpiAreas,
  selectedDueDateFilters,
  selectedRetailers,
  displaySettings,
})
</script>

<style scoped>
/* Mobile improvements for MultiSelect */
@media (max-width: 639px) {
  :deep(.p-multiselect) {
    min-height: 44px;
  }

  :deep(.p-multiselect-label) {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  :deep(.p-multiselect-token) {
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
  }

  :deep(.p-multiselect-panel) {
    width: calc(100vw - 2rem) !important;
    max-width: 400px !important;
  }

  :deep(.p-multiselect-items-wrapper) {
    max-height: 50vh;
  }
}

/* Improved checkbox touch targets on mobile */
@media (max-width: 639px) {
  :deep(.p-checkbox) {
    width: 1.5rem;
    height: 1.5rem;
  }

  :deep(.p-checkbox-box) {
    width: 1.5rem;
    height: 1.5rem;
  }
}

/* Custom scrollbar for filter lists */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--p-surface-300) var(--p-surface-100);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: var(--p-surface-100);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--p-surface-300);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--p-surface-400);
}
</style>
